import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { requireAuth } from "../lib/requireAuth";
import { PageEyebrow } from "../components/PageEyebrow";
import { Centered } from "../components/Centered";

export const Route = createFileRoute("/progress/exercise/$exerciseId")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: ExerciseProgressPage,
});

function ExerciseProgressPage() {
  const { exerciseId } = Route.useParams();
  const idNum = Number(exerciseId);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["exercise-progress", idNum],
    queryFn: async () => {
      const { data, error } = await api.GET(
        "/exercises/{exercise_id}/progress",
        { params: { path: { exercise_id: idNum } } },
      );
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });

  if (isLoading) return <Centered>Loading attempts…</Centered>;
  if (isError || !data)
    return <Centered>Could not load #{exerciseId}.</Centered>;

  const attempts = data.attempts ?? [];

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow
        label={
          <Link
            to="/history"
            className="text-ink-500 hover:text-ink-900 transition-colors"
          >
            ← history
          </Link>
        }
        mono={`#${exerciseId} · ${data.exerciseType}`}
      />
      <h1 className="font-serif text-2xl text-ink-900 mb-2 tracking-tight">
        {data.prompt}
      </h1>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-xl">
        Expected:{" "}
        <code className="font-mono bg-ink-50 px-1 rounded text-ink-700">
          {data.expected}
        </code>{" "}
        · You've attempted this exercise{" "}
        <span className="tabular-nums">{attempts.length}</span> time
        {attempts.length === 1 ? "" : "s"}.
      </p>
      <div className="hairline mb-6 sm:mb-8" />

      <Link
        to="/practice/$exerciseId"
        params={{ exerciseId: String(idNum) }}
        className="inline-block mb-6 bg-accent px-4 py-2 text-accent-fg text-sm hover:bg-accent-hover transition-colors"
      >
        Try it again →
      </Link>

      {attempts.length === 0 ? (
        <p className="text-ink-500">No attempts yet.</p>
      ) : (
        <ul className="space-y-3">
          {[...attempts].reverse().map((a) => (
            <li
              key={a.attemptId}
              className="border border-ink-100 bg-paper p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-2">
                <div className="flex items-baseline gap-3">
                  <span className="text-ink-400 text-xs tabular-nums">
                    {formatTs(a.ts)}
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      a.correct ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {a.correct ? "Correct" : "Not quite"}
                  </span>
                </div>
                <div className="text-xs text-ink-400 tabular-nums">
                  scaffolding {a.scaffoldingLevel}
                  {a.responseTimeMs != null && (
                    <> · {(a.responseTimeMs / 1000).toFixed(1)}s</>
                  )}
                </div>
              </div>
              <div className="text-sm mb-2">
                <span className="text-ink-400">You answered:</span>{" "}
                <code className="font-mono bg-ink-50 px-1 rounded text-ink-900">
                  {a.response?.trim() ? a.response : "(empty)"}
                </code>
              </div>
              {a.skills.length > 0 && (
                <ul className="text-xs text-ink-500 space-y-0.5">
                  {a.skills.map((s) => (
                    <li key={s.skillCode} className="flex items-baseline gap-2">
                      <span
                        className={
                          s.passed ? "text-emerald-600" : "text-rose-600"
                        }
                      >
                        {s.passed ? "✓" : "✗"}
                      </span>
                      <Link
                        to="/progress/skill/$skillCode"
                        params={{ skillCode: s.skillCode }}
                        className="text-ink-700 hover:text-ink-900"
                      >
                        {s.skillName}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
